// API client gamificación · XP + quizzes + leaderboard + rewards
// Sprint campus-fase3-gamificacion · 2026-05-30 · Carlos Beltrán
//
// Mismo patrón que `api.ts`: buildHeaders + handleResponse. Tipado estricto.

import type { ApiError, AuthIdentity } from './types';
import { isStubModeAllowed } from './env';
import { getImpersonationToken } from './impersonation';

const API_BASE = '/api';

// ──────────────────────────────────────────────────────────────────────────
// Types
// ──────────────────────────────────────────────────────────────────────────

export interface XpSnapshot {
  total_xp: number;
  level: number;
  xp_into_level: number;
  xp_to_next_level: number;
  xp_floor_current_level: number;
  xp_floor_next_level: number;
  current_streak?: number;
  // Rango = PLAN/HITO del alumno (resuelto por el backend), independiente del
  // nivel de XP. `rank` es el slug (cadete · piloto · piloto_basic · piloto_pro
  // · comandante) y `rank_label` el texto a mostrar. Opcionales por
  // compatibilidad con backends antiguos.
  rank?: string;
  rank_label?: string;
  // Ofertas desbloqueadas por HITO (las resuelve el backend, reutilizando la
  // misma señal que rank_engine). Opcionales por compatibilidad con backends
  // antiguos que aún no las emiten.
  //   basic_offer_unlocked = completó Robotea Start EN REAL y NO tiene Basic.
  //   pro_offer_unlocked   = completó Robotea Basic y NO tiene Pro.
  // Cuando son true, la UI ofrece la URL -especial (20%) del tier correspondiente.
  basic_offer_unlocked?: boolean;
  pro_offer_unlocked?: boolean;
}

export interface XpEvent {
  id: string;
  event_kind:
    | 'lesson_completed'
    | 'module_completed'
    | 'quiz_passed'
    | 'quiz_perfect'
    | 'streak_milestone'
    | 'first_login';
  reference_id: string;
  xp_delta: number;
  created_at: string;
}

export interface XpEventsResponse {
  events: XpEvent[];
  count: number;
}

export interface QuizQuestion {
  id: string;
  position: number;
  question: string;
  options: string[];
}

export interface QuizLastAttempt {
  correct_count: number;
  total_count: number;
  passed: boolean;
  is_perfect: boolean;
  created_at: string;
}

export interface LessonQuizResponse {
  lesson_id: string;
  questions: QuizQuestion[];
  total: number;
  last_attempt: QuizLastAttempt | null;
}

export interface QuizPerQuestionResult {
  question_id: string;
  selected_index: number | null;
  correct_index: number;
  is_correct: boolean;
  explanation: string | null;
}

export interface QuizAttemptResponse {
  attempt_id: string;
  created_at: string;
  correct_count: number;
  total_count: number;
  passed: boolean;
  is_perfect: boolean;
  per_question: QuizPerQuestionResult[];
  xp_awarded: number;
  leveled_up: boolean;
  current_level: number | null;
  granted_reward_ids: string[];
}

export interface RewardSummary {
  id: string;
  kind: 'coupon' | 'badge' | 'discount_code' | 'custom';
  threshold_level: number;
  title: string;
  description: string | null;
  payload: Record<string, unknown>;
  earned_at?: string;
  redeemed_at?: string | null;
}

export interface RewardsResponse {
  earned: RewardSummary[];
  upcoming: RewardSummary[];
  current_level: number;
  next_milestone: number | null;
}

export interface LeaderboardEntry {
  rank: number;
  display_name: string;
  level: number;
  xp_weekly: number;
}

export interface LeaderboardResponse {
  entries: LeaderboardEntry[];
  window_days: number;
  total: number;
}

export interface AllTimeLeaderboardEntry {
  rank: number;
  display_name: string;
  level: number;
  total_xp: number;
}

export interface AllTimeLeaderboardResponse {
  entries: AllTimeLeaderboardEntry[];
  total: number;
}

export interface OptInResponse {
  leaderboard_opt_in: boolean;
}

// ──────────────────────────────────────────────────────────────────────────
// Internal helpers
// ──────────────────────────────────────────────────────────────────────────

function buildHeaders(auth: AuthIdentity | null): HeadersInit {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  // Impersonación "ver como alumno" (F3): prioridad sobre el JWT real del admin.
  // Sin esto, los endpoints de gamificación (XP/rango/eventos/rewards) devolvían
  // los datos del ADMIN en vista-alumno (leak). Mismo patrón que api.ts.
  const impersonationToken = getImpersonationToken();
  if (impersonationToken) {
    headers['Authorization'] = `Bearer ${impersonationToken}`;
    return headers;
  }
  if (!auth) return headers;
  if (auth.mode === 'supabase' && auth.jwt) {
    headers['Authorization'] = `Bearer ${auth.jwt}`;
  } else if (auth.mode === 'stub' && isStubModeAllowed()) {
    headers['X-User-Email'] = auth.email;
  }
  return headers;
}

async function handle<T>(res: Response): Promise<T> {
  if (res.ok) return (await res.json()) as T;
  let detail = `HTTP ${res.status}`;
  try {
    const body = (await res.json()) as { detail?: string };
    if (typeof body.detail === 'string') detail = body.detail;
  } catch {
    /* ignore */
  }
  const err: ApiError = { detail, status: res.status };
  throw err;
}

// ──────────────────────────────────────────────────────────────────────────
// XP
// ──────────────────────────────────────────────────────────────────────────

export async function fetchMyXp(auth: AuthIdentity | null): Promise<XpSnapshot> {
  const res = await fetch(`${API_BASE}/me/xp`, {
    headers: buildHeaders(auth),
  });
  return handle<XpSnapshot>(res);
}

export async function fetchMyXpEvents(
  auth: AuthIdentity | null,
  limit = 20,
): Promise<XpEventsResponse> {
  const res = await fetch(`${API_BASE}/me/xp/events?limit=${limit}`, {
    headers: buildHeaders(auth),
  });
  return handle<XpEventsResponse>(res);
}

// ──────────────────────────────────────────────────────────────────────────
// Quizzes
// ──────────────────────────────────────────────────────────────────────────

export async function fetchLessonQuiz(
  auth: AuthIdentity | null,
  lessonId: string,
): Promise<LessonQuizResponse> {
  const res = await fetch(`${API_BASE}/lessons/${lessonId}/quiz`, {
    headers: buildHeaders(auth),
  });
  return handle<LessonQuizResponse>(res);
}

export async function submitQuizAttempt(
  auth: AuthIdentity | null,
  lessonId: string,
  answers: Record<string, number>,
): Promise<QuizAttemptResponse> {
  const res = await fetch(`${API_BASE}/lessons/${lessonId}/quiz/attempt`, {
    method: 'POST',
    headers: buildHeaders(auth),
    body: JSON.stringify({ answers }),
  });
  return handle<QuizAttemptResponse>(res);
}

// ──────────────────────────────────────────────────────────────────────────
// Rewards
// ──────────────────────────────────────────────────────────────────────────

export async function fetchMyRewards(auth: AuthIdentity | null): Promise<RewardsResponse> {
  const res = await fetch(`${API_BASE}/me/rewards`, {
    headers: buildHeaders(auth),
  });
  return handle<RewardsResponse>(res);
}

export async function redeemReward(
  auth: AuthIdentity | null,
  rewardId: string,
): Promise<{ reward_id: string; earned_at: string; redeemed_at: string }> {
  const res = await fetch(`${API_BASE}/me/rewards/${rewardId}/redeem`, {
    method: 'POST',
    headers: buildHeaders(auth),
  });
  return handle(res);
}

// ──────────────────────────────────────────────────────────────────────────
// Leaderboard
// ──────────────────────────────────────────────────────────────────────────

export async function fetchWeeklyLeaderboard(
  auth: AuthIdentity | null,
  limit = 10,
): Promise<LeaderboardResponse> {
  const res = await fetch(`${API_BASE}/leaderboard/weekly?limit=${limit}`, {
    headers: buildHeaders(auth),
  });
  return handle<LeaderboardResponse>(res);
}

export async function fetchAllTimeLeaderboard(
  auth: AuthIdentity | null,
  limit = 10,
): Promise<AllTimeLeaderboardResponse> {
  const res = await fetch(`${API_BASE}/leaderboard/all-time?limit=${limit}`, {
    headers: buildHeaders(auth),
  });
  return handle<AllTimeLeaderboardResponse>(res);
}

export async function fetchMyLeaderboardOptIn(
  auth: AuthIdentity | null,
): Promise<OptInResponse> {
  const res = await fetch(`${API_BASE}/me/leaderboard-opt-in`, {
    headers: buildHeaders(auth),
  });
  return handle<OptInResponse>(res);
}

export async function patchMyLeaderboardOptIn(
  auth: AuthIdentity | null,
  optIn: boolean,
): Promise<OptInResponse> {
  const res = await fetch(`${API_BASE}/me/leaderboard-opt-in`, {
    method: 'PATCH',
    headers: buildHeaders(auth),
    body: JSON.stringify({ opt_in: optIn }),
  });
  return handle<OptInResponse>(res);
}
